// @flow strict

import * as React from 'react'
import { type PropsWithChildren } from '@mondough/public-web-components'
import { throttle } from 'throttle-debounce'

const viewportContext = React.createContext<{ width: ?number }>({})

export const useViewport = () => {
  const { width } = React.useContext(viewportContext)
  return { width }
}

// Note: If we ever need the viewport height we should add it here
const ViewportProvider = ({ children }: PropsWithChildren<{}>) => {
  /* We need to check for the existence of window before we try to calculate the viewport width 
  because we server-side render and window won't be available in that case. Make sure to check for 
  window using `typeof window !== 'undefined'`. Also worth noting that React Hooks does NOT allow hooks 
  inside of conditionals, the conditional has to be inside the hook. */
  const [width, setWidth] = React.useState(
    typeof window === 'undefined' ? null : window.innerWidth,
  )
  const handleWindowResize = () => {
    if (typeof window === 'undefined') return
    setWidth(window.innerWidth)
  }

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    window.addEventListener('resize', throttle(100, handleWindowResize))
    return () =>
      window.removeEventListener('resize', throttle(100, handleWindowResize))
  }, [])

  return (
    <viewportContext.Provider value={{ width }}>
      {children}
    </viewportContext.Provider>
  )
}

export default ViewportProvider
